import React from "react";

import { Modal, ModalBody } from "reactstrap";

import { getRegiao } from "../TrilhaLudica";

// Quantas cartas o leque mostra. Não são as 22: o leque existe para o professor
// VER o que a região entrega, e vinte e duas cartas sobrepostas viram uma pilha
// ilegível. Sete é o que cabe num arco de 60° com cada carta ainda mostrando a
// própria ilustração.
const CARTAS_NO_LEQUE = 7;

// A abertura do leque, em graus. 54° distribuídos entre sete cartas dá 9° de
// diferença entre vizinhas — o bastante para o olho ler "leque" e não "pilha".
const ABERTURA = 54;

/**
 * O que a região entrega, para o professor decidir com conhecimento.
 *
 * A escolha da região não muda depois da criação e define três coisas que o
 * professor não via antes de escolher: o CENÁRIO que o aluno percorre, o MASCOTE
 * que fala com ele e as 22 CARTAS que ele coleciona. Um `<select>` com "Norte —
 * Amazônia" pedia uma decisão irreversível sobre conteúdo invisível.
 *
 * É um painel, e não um tooltip de passar o mouse: são três blocos de conteúdo
 * (cenário, mascote com o alerta da espécie, e o acervo de cartas), o professor
 * precisa de tempo para ler, e em tablet não existe hover. O leque é que responde
 * ao ponteiro — ali o gesto cabe, porque a carta já está na tela e só cresce.
 *
 * @param {string}   regiaoId  id da região a mostrar
 * @param {Function} onFechar
 */
function OverviewDaRegiao({ regiaoId, onFechar }) {
  const regiao = getRegiao(regiaoId);
  const {
    mascote,
    tabuleiro,
    cores,
    cardsComuns = [],
    cardsRaros = []
  } = regiao;

  // O leque alterna problema e solução, que é como o acervo é montado (10 de
  // cada). Pegar os sete primeiros da lista mostraria só problemas, e a região
  // pareceria um catálogo de más notícias.
  const problemas = cardsComuns.filter(card => card.tipo === "problema");
  const solucoes = cardsComuns.filter(card => card.tipo === "solucao");
  const doLeque = [];
  for (let i = 0; doLeque.length < CARTAS_NO_LEQUE; i += 1) {
    if (problemas[i]) doLeque.push(problemas[i]);
    if (solucoes[i] && doLeque.length < CARTAS_NO_LEQUE)
      doLeque.push(solucoes[i]);
    if (!problemas[i] && !solucoes[i]) break;
  }

  // O alerta da espécie sai da carta RARA de ameaça — o mesmo texto que o aluno
  // recebe do mascote no meio da trilha. Sem duplicar em lugar nenhum: se o texto
  // mudar lá, muda aqui.
  const ameaca = cardsRaros.find(card => !card.comemorar) || cardsRaros[0];

  const total = cardsComuns.length + cardsRaros.length;
  const meio = (doLeque.length - 1) / 2;

  return (
    // `scrollable` põe o `modal-dialog-scrollable` do Bootstrap: o painel para de
    // crescer com o conteúdo e passa a rolar POR DENTRO. Sem ele, uma região com
    // vinte assuntos empurrava o rodapé do painel para fora da tela e quem rolava
    // era a página do AVA, com o cabeçalho do modal indo junto.
    //
    // As variáveis de cor vão no `.modal` externo porque custom properties
    // HERDAM: assim o cenário (que é filho direto do `.modal-content`) e o corpo
    // as recebem sem precisar de um wrapper em volta dos dois.
    <Modal
      isOpen
      toggle={onFechar}
      size="lg"
      scrollable
      className="ct-overview__modal"
      style={{
        "--ct-overview-cor": cores.chromeBorda,
        "--ct-overview-cor-forte": cores.chromeTexto,
        "--ct-overview-cor-clara": cores.chromeFundo
      }}
    >
      <button
        type="button"
        className="close ct-overview__fechar"
        onClick={onFechar}
        aria-label="Fechar"
      >
        <span aria-hidden="true">&times;</span>
      </button>

      {/* Filho DIRETO do `.modal-content`, e não do corpo: dentro do
          `.modal-body` ele herdava o padding lateral do tema do AVA e sobrava
          uma faixa branca na direita — o harness não reproduzia porque carrega
          só o Bootstrap, e o AVA empilha dois CoreUI por cima. Fora do corpo,
          nenhum padding o alcança, e de quebra ele fica FIXO enquanto o resto
          rola. */}
      <header
        className="ct-overview__cenario"
        style={{ backgroundImage: `url(${tabuleiro.fundo})` }}
      >
        <img
          className="ct-overview__mascote"
          src={mascote.corpo}
          alt={mascote.nome}
        />
        <div className="ct-overview__identidade">
          <span className="ct-overview__regiao">{regiao.nome}</span>
          <h4 className="ct-overview__bioma">{regiao.bioma}</h4>
          <span className="ct-overview__mascote-nome">{mascote.nome}</span>
        </div>
      </header>

      <ModalBody className="ct-overview__corpo">
        {ameaca && (
          <section className="ct-overview__alerta">
            <h5>{ameaca.nome}</h5>
            <p>{ameaca.pilula}</p>
          </section>
        )}

        <section>
          <h5 className="ct-overview__titulo">
            {`As cartas que o aluno coleciona — ${total} nesta região`}
          </h5>
          <p className="ct-overview__legenda">
            Cada fase premiada entrega uma. Passe o mouse para ver o assunto.
          </p>

          <div className="ct-overview__leque">
            {doLeque.map((carta, indice) => (
              <figure
                key={carta.id}
                className={`ct-overview__carta ct-overview__carta--${carta.tipo}`}
                style={{
                  // O arco é calculado, e não escrito à mão: a lista pode
                  // encurtar (região com menos cartas) sem o leque entortar.
                  "--ct-giro": `${
                    (indice - meio) * (ABERTURA / doLeque.length)
                  }deg`,
                  "--ct-subida": `${Math.abs(indice - meio) * 6}px`,
                  zIndex: doLeque.length - Math.abs(indice - meio)
                }}
              >
                <img src={carta.asset} alt="" aria-hidden="true" />
                <figcaption>
                  <strong>{carta.nome}</strong>
                  <span>{carta.pilula}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="ct-overview__assuntos-secao">
          <h5 className="ct-overview__titulo">Assuntos da região</h5>
          {/* Em DOIS blocos, e não numa lista só: o acervo é feito de pares —
              dez do que ameaça o bioma, dez do que o protege —, e essa simetria
              é o desenho pedagógico da região. Misturados, os vinte viravam uma
              nuvem de palavras em que a cor era o único sinal do que separa um
              grupo do outro, e cor sozinha não nomeia nada. */}
          <div className="ct-overview__grupos">
            <div className="ct-overview__grupo ct-overview__grupo--problema">
              <h6>O que ameaça o bioma</h6>
              <ul>
                {problemas.map(carta => (
                  <li key={carta.id} title={carta.pilula}>
                    {carta.nome}
                  </li>
                ))}
              </ul>
            </div>
            <div className="ct-overview__grupo ct-overview__grupo--solucao">
              <h6>O que ajuda a proteger</h6>
              <ul>
                {solucoes.map(carta => (
                  <li key={carta.id} title={carta.pilula}>
                    {carta.nome}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </ModalBody>
    </Modal>
  );
}

export default OverviewDaRegiao;
